import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    trackModeOpened,
    trackModeClosed,
    trackEntryOpened,
    trackChipTraversed,
} from "./telemetry";

/**
 * InsightContext — the central state for "Help & Insights" mode.
 *
 * This is a pure UI overlay layer. Activating Insight Mode does NOT mutate
 * routes, remount pages, reset scroll, or interrupt any workflow underneath.
 * The portal stays mounted and frozen; the veil sits in front.
 *
 * Public surface (kept intentionally small):
 *   - active     : boolean
 *   - target     : insight id currently displayed in the rail (string | null)
 *   - locale     : "en" | "fr" | "es" — REACTIVE, follows i18n.language
 *   - toggle()   : light-switch on/off
 *   - open(id)   : set the rail target (must already be in mode)
 *   - close()    : exit mode
 *   - setTarget(id) : swap rail target without leaving mode
 *
 * Locale: derived from react-i18next's `i18n.language` on every render so
 * the rail follows live language switches. The `defaultLocale` prop is now
 * a soft fallback only (used when i18n is not yet initialized).
 */

// Normalize i18n language codes (e.g. "fr-CA", "en-US") to the registry's
// three supported buckets: "en" | "fr" | "es". Anything else falls to "en".
const normalizeLang = (raw) => {
    if (!raw) return "en";
    const base = String(raw).toLowerCase().split("-")[0];
    return base === "fr" ? "fr" : base === "es" ? "es" : "en";
};

const InsightContext = createContext(null);

export const InsightProvider = ({ children, defaultLocale = "en" }) => {
    const { i18n } = useTranslation();
    const [active, setActive] = useState(false);
    const [target, setTarget] = useState(null);
    // Locale is derived live from i18n.language (the app's source of truth
    // for the operator's current language). `defaultLocale` only applies
    // when i18n has not yet emitted a language.
    const locale = useMemo(
        () => normalizeLang(i18n?.language || defaultLocale),
        [i18n?.language, defaultLocale]
    );
    // Last-known target — kept in a ref so chip traversals can record
    // the `from_key` even when the next setTarget happens in the same tick.
    const lastTargetRef = useRef(null);
    // First-run hint: shown the first time a user enters Insight Mode.
    // Stored in localStorage so it never appears again after dismissal.
    const [firstRunSeen, setFirstRunSeen] = useState(() => {
        try {
            return localStorage.getItem("insight.firstRunSeen") === "1";
        } catch {
            return true;
        }
    });

    const markFirstRunSeen = useCallback(() => {
        setFirstRunSeen(true);
        try {
            localStorage.setItem("insight.firstRunSeen", "1");
        } catch {
            /* ignore */
        }
    }, []);

    const close = useCallback(() => {
        setActive((prev) => {
            if (prev) trackModeClosed();
            return false;
        });
        setTarget(null);
        lastTargetRef.current = null;
    }, []);

    const toggle = useCallback(() => {
        setActive((prev) => {
            if (prev) {
                trackModeClosed();
                setTarget(null);
                lastTargetRef.current = null;
                return false;
            }
            trackModeOpened();
            return true;
        });
    }, []);

    const open = useCallback((id) => {
        const next = id || null;
        if (next) {
            const prevKey = lastTargetRef.current;
            if (prevKey && prevKey !== next) {
                // Chip / directory hop — record the path shape first, then
                // the new entry as opened. Repeated opens of the same key
                // are silently coalesced.
                trackChipTraversed(prevKey, next);
                trackEntryOpened(next);
            } else if (!prevKey) {
                trackEntryOpened(next);
            }
            lastTargetRef.current = next;
        } else {
            lastTargetRef.current = null;
        }
        setTarget(next);
    }, []);

    // ESC always exits — light-switch behavior.
    useEffect(() => {
        if (!active) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                close();
            }
        };
        document.addEventListener("keydown", onKey, true);
        return () => document.removeEventListener("keydown", onKey, true);
    }, [active, close]);

    const value = useMemo(
        () => ({
            active,
            target,
            locale,
            firstRunSeen,
            markFirstRunSeen,
            toggle,
            open,
            close,
            setTarget,
        }),
        [active, target, locale, firstRunSeen, markFirstRunSeen, toggle, open, close]
    );

    return <InsightContext.Provider value={value}>{children}</InsightContext.Provider>;
};

export const useInsight = () => {
    const ctx = useContext(InsightContext);
    if (!ctx) {
        throw new Error("useInsight must be used within InsightProvider");
    }
    return ctx;
};
